/**
 * Writing the reply to judge against, when the run has none: a ticket nobody answered, a fabricated
 * scenario, or no scenario at all. Folded until wanted, so a textarea is not sitting open under every
 * AI reply. The draft is component state: a reference reply is run output, and Reset takes it along.
 */

import { useEffect, useRef, useState } from 'react';
import { PenLine, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PillButton } from '../ui/pill-button';
import { ReviewInvite } from './review-invite';

/** What is missing depends on what the run replays, so the copy does too. */
const MISSING = {
  ticket: 'Nobody answered this ticket',
  fabricated: 'Nobody answered this fabricated customer',
  none: 'No ticket behind this run',
} as const;

interface ComposeReplyProps {
  /** What the run replays: a past ticket, a fabricated scenario, or nothing. */
  behind: 'ticket' | 'fabricated' | null;
  /** What was typed the last time this run was judged, so a retry after a failure does not ask for it twice. */
  defaultValue?: string;
  onCompare: (reply: string) => void;
}

export const ComposeReply = ({ behind, defaultValue = '', onCompare }: ComposeReplyProps) => {
  // Seeded at mount only: a returning draft arrives with the remount, never after it.
  const [open, setOpen] = useState(defaultValue !== '');
  const [draft, setDraft] = useState(defaultValue);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  const ready = draft.trim().length > 0;
  const submit = () => {
    if (ready) onCompare(draft.trim());
  };

  if (!open) {
    return (
      <ReviewInvite
        icon={PenLine}
        title={MISSING[behind ?? 'none']}
        description='Write the reply you would have sent, and compare the AI against it.'
        action='Write a reply'
        onAction={() => setOpen(true)}
      />
    );
  }

  return (
    <div className='flex flex-col gap-2 rounded-xl border border-gray-500 bg-white px-3.5 py-3 animate-in fade-in-0 slide-in-from-bottom-1 duration-300 ease-out'>
      <div className='flex items-center justify-between gap-2'>
        <span className='flex items-center gap-1.5 text-[13px] leading-4.5 font-medium text-text-body'>
          <PenLine className='size-3.5 text-gray-800' aria-hidden />
          The reply to compare against
        </span>
        <button
          type='button'
          onClick={() => setOpen(false)}
          className='rounded-full p-1 text-gray-800 transition-colors hover:bg-gray-100 hover:text-text-heading'
          aria-label='Discard this reply'
        >
          <X className='size-3.5' aria-hidden />
        </button>
      </div>

      <textarea
        ref={field}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
        }}
        rows={4}
        placeholder='Write the reply a human agent would have sent…'
        className={cn(
          'w-full resize-y rounded-lg border border-gray-500 bg-white px-2.5 py-2 text-[13px] leading-5 text-text-body',
          'placeholder:text-gray-800 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-hidden'
        )}
      />

      <div className='flex items-center justify-between gap-3'>
        <span className='text-[11px] leading-4 text-gray-800'>
          Judged as the reference reply, not as something that was actually sent.
        </span>
        <PillButton variant='primary' className='shrink-0' disabled={!ready} onClick={submit}>
          Compare
        </PillButton>
      </div>
    </div>
  );
};
